import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Layers,
  FolderTree
} from 'lucide-react';
import { domainsApi } from '../services/api';
import { Domain, DomainCreate, DomainUpdate } from '../types';

interface DomainsProps {
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

const Domains: React.FC<DomainsProps> = ({ addNotification }) => {
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDomain, setEditingDomain] = useState<Domain | null>(null);
  const [formData, setFormData] = useState<DomainCreate>({
    name: '',
    description: '',
    layer: undefined
  });

  const layerOptions = [
    { value: 'L1: Knowledge Layer', label: 'L1: Knowledge Layer', desc: 'Knowledge Source / Content Validation' },
    { value: 'L2: Retrieval Layer', label: 'L2: Retrieval Layer', desc: 'RAG Pipeline Evaluation' },
    { value: 'L3: Reasoning Layer', label: 'L3: Reasoning Layer', desc: 'Agentic / Multi-Step Reasoning' },
    { value: 'L4: Interaction Layer', label: 'L4: Interaction Layer', desc: 'Intent & Query Understanding' },
    { value: 'L5: Robustness Layer', label: 'L5: Robustness Layer', desc: 'Adversarial / Stress Testing' }
  ];

  // Sort domains by layer order (L1..L5, then Unassigned) for table display
  const layerOrder = layerOptions.map((o) => o.value);
  const sortedDomains = React.useMemo(() => {
    return [...domains].sort((a, b) => {
      const ai = a.layer ? layerOrder.indexOf(a.layer) : layerOrder.length;
      const bi = b.layer ? layerOrder.indexOf(b.layer) : layerOrder.length;
      if (ai !== bi) return ai - bi;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [domains]);

  useEffect(() => {
    loadDomains();
  }, []);

  const loadDomains = async () => {
    try {
      setLoading(true);
      const response = await domainsApi.list();
      setDomains(response.domains);
    } catch (error) {
      addNotification('Failed to load domains', 'error');
      console.error('Error loading domains:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDomain = async () => {
    try {
      if (!formData.name.trim()) {
        addNotification('Domain name is required', 'error');
        return;
      }

      await domainsApi.create(formData);
      addNotification('Domain created successfully', 'success');
      setShowCreateModal(false);
      setFormData({ name: '', description: '', layer: undefined });
      loadDomains();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to create domain';
      addNotification(message, 'error');
      console.error('Error creating domain:', error);
    }
  };

  const handleEditDomain = async () => {
    if (!editingDomain) return;

    try {
      if (!formData.name.trim()) {
        addNotification('Domain name is required', 'error');
        return;
      }

      const updateData: DomainUpdate = {
        name: formData.name,
        description: formData.description || undefined,
        layer: formData.layer ?? undefined
      };

      await domainsApi.update(editingDomain.id, updateData);
      addNotification('Domain updated successfully', 'success');
      setShowEditModal(false);
      setEditingDomain(null);
      setFormData({ name: '', description: '', layer: undefined });
      loadDomains();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to update domain';
      addNotification(message, 'error');
      console.error('Error updating domain:', error);
    }
  };

  const handleDeleteDomain = async (domainId: number, domainName: string) => {
    if (!window.confirm(`Are you sure you want to delete domain "${domainName}"? This will also affect all its categories and techniques.`)) {
      return;
    }

    try {
      await domainsApi.delete(domainId);
      addNotification('Domain deleted successfully', 'success');
      loadDomains();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to delete domain';
      addNotification(message, 'error');
      console.error('Error deleting domain:', error);
    }
  };

  const openEditModal = (domain: Domain) => {
    setEditingDomain(domain);
    setFormData({
      name: domain.name,
      description: domain.description || '',
      layer: domain.layer
    });
    setShowEditModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Layers className="h-8 w-8 text-indigo-600" />
            <div>
              <h1 className="text-2xl font-bold text-slate-100">Domains</h1>
              <p className="text-slate-300 mt-1">Top-level organization for test hierarchy (Domain → Category → Technique)</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Domain
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <FolderTree className="h-5 w-5 text-indigo-400 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-slate-200">About Domains</h4>
            <p className="text-sm text-slate-300 mt-1">
              Domains are the highest level of test organization, grouped by layer (L1–L5). Each domain contains categories, which contain techniques.
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {domains.length} domain{domains.length !== 1 ? 's' : ''} across layers
            </p>
          </div>
        </div>
      </div>

      {/* Domains Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <p className="mt-2 text-slate-400">Loading domains...</p>
        </div>
      ) : (
        <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-700">
              <thead className="bg-slate-700/50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Layer</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Name</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Description</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Categories</th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-slate-800 divide-y divide-slate-700">
                {sortedDomains.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-slate-500 text-sm">
                      No domains yet. Create one to get started.
                    </td>
                  </tr>
                ) : (
                  sortedDomains.map((domain) => (
                    <tr key={domain.id} className="hover:bg-slate-700/30">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {domain.layer ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-indigo-600/30 text-indigo-200">
                            <Layers className="h-3 w-3 mr-1" />
                            {domain.layer}
                          </span>
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-slate-100">{domain.name}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-300 max-w-md truncate" title={domain.description || ''}>
                        {domain.description || <span className="text-slate-500">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                        {domain.categories_count ?? 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => openEditModal(domain)}
                            className="p-1.5 text-slate-400 hover:text-indigo-400 rounded"
                            title="Edit domain"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteDomain(domain.id, domain.name)}
                            className="p-1.5 text-slate-400 hover:text-red-400 rounded"
                            title="Delete domain"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-slate-800 border border-slate-700">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-slate-100 mb-4">Create New Domain</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Domain Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="e.g., Knowledge Base"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Layer
                  </label>
                  <select
                    value={formData.layer || ''}
                    onChange={(e) => setFormData({ ...formData, layer: e.target.value || undefined })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">Select a layer...</option>
                    {layerOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label} - {option.desc}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Assign a layer classification (L1-L5) to this domain
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Describe the purpose of this domain..."
                  />
                </div>
                
              </div>
              
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({ name: '', description: '', layer: undefined });
                  }}
                  className="px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 border border-slate-600 rounded-md hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateDomain}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
                >
                  Create Domain
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingDomain && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-slate-800 border border-slate-700">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-slate-100 mb-4">Edit Domain</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Domain Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Layer
                  </label>
                  <select
                    value={formData.layer || ''}
                    onChange={(e) => setFormData({ ...formData, layer: e.target.value || undefined })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">Select a layer...</option>
                    {layerOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label} - {option.desc}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Assign a layer classification (L1-L5) to this domain
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingDomain(null);
                    setFormData({ name: '', description: '', layer: undefined });
                  }}
                  className="px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 border border-slate-600 rounded-md hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditDomain}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
                >
                  Update Domain
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Domains;

